import { getSiteBySlug, type SiteResponse } from '@lms/api-client/backend';
import { ChartBrandProvider } from '@lms/charts';
import { applyBrandColors } from '@lms/ui/branding';
import { createContext, type ReactNode, useContext, useEffect } from 'react';
import useSWR from 'swr';
import safeLogo from '@/assets/safe_logo.webp';

export type Site = SiteResponse;

// The default Safe brand, shown before a site loads or when a slug has no
// matching site. Mirrors `placeholderBrand` from the Next app's branding DAL.
export const placeholderBrand: Site = {
  id: 0,
  name: 'SAFE Asset Group',
  slug: 'safe',
  logo: safeLogo,
  primaryColor: '#00a586',
  secondaryColor: '#00a586',
  hasGroups: true,
  groupAlias: 'events',
  poster: null,
  features: { grouplogo: false },
  siteType: 'location',
};

interface BrandContextValue {
  brand: Site;
  slug: string;
  loading: boolean;
  notFound: boolean;
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

export function useBrand(): BrandContextValue {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within BrandProvider');
  return ctx;
}

export function BrandProvider({
  slug,
  children,
}: {
  slug: string;
  children: ReactNode;
}) {
  const { data, isLoading } = useSWR(slug ? ['site', slug] : null, () =>
    getSiteBySlug(slug),
  );

  useEffect(() => {
    if (data) applyBrandColors(data.primaryColor);
  }, [data]);

  // Gate on load so children never render with the placeholder brand. Before
  // this, admin pages fired queries and seeded forms with the placeholder id/
  // slug (id 0, "safe"), crashing the dashboard and mis-targeting settings.
  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!data) {
    // The slug does not resolve to a site (unknown tenant or fetch error).
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-2 p-8 text-center">
        <h1 className="text-2xl font-semibold">Site not found</h1>
        <p className="text-muted-foreground">No site matches “{slug}”.</p>
      </div>
    );
  }

  return (
    <BrandContext.Provider
      value={{ brand: data, slug, loading: false, notFound: false }}
    >
      <ChartBrandProvider
        primary={data.primaryColor}
        secondary={data.secondaryColor}
      >
        {children}
      </ChartBrandProvider>
    </BrandContext.Provider>
  );
}
